import axios from "axios";

console.log('API Environment:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV
});

// Import store and clearAuth action - we'll set this after store creation to avoid circular imports
let store = null;
let clearAuthAction = null;

// Function to set store and action references after store creation
export const setStoreReference = (storeInstance, clearAuth) => {
  store = storeInstance;
  clearAuthAction = clearAuth;
};

const api = axios.create({
  // When accessing through promotly.cloud, always use the /api path
  baseURL: window.location.hostname === 'localhost'
    ? 'http://localhost:4000'
    : '/api',
  withCredentials: true  // CRITICAL: Enable sending cookies with requests
});

// Add request interceptor to include session cookie
api.interceptors.request.use((config) => {
  // Include session cookie for authentication (session_id)
  // Cookies are automatically sent with requests
  console.log('Making API request to:', config.baseURL + config.url);
  return config;
}, (error) => {
  return Promise.reject(error);
});

// ---------------------------------------------------------------------------
// Forced re-login plumbing
// ---------------------------------------------------------------------------

/**
 * sessionStorage key the login page reads to explain a forced re-login.
 * Written by the response interceptor, consumed (and cleared) by the login page.
 * Shape: { code, subdomain, targetAccountId, at }
 */
export const AUTH_MISMATCH_REASON_KEY = 'auth_mismatch_reason';

// Guard against redirect loops (cyclic auth-guard reload): never fire more
// than one login redirect within a short window.
let pendingAuthRedirect = false;

/**
 * Send the user to a login page exactly once.
 *
 * `reason.subdomain` — when present (ACCOUNT_CONTEXT_MISMATCH carries the TARGET
 * tenant), the user is sent to THAT tenant's login, because that is the account
 * they were trying to reach. Otherwise the current host's login is used.
 */
const redirectToLogin = (reason) => {
  if (pendingAuthRedirect) return;
  pendingAuthRedirect = true;

  // Stash the reason so the login page can show why they were signed out.
  try {
    if (reason) sessionStorage.setItem(AUTH_MISMATCH_REASON_KEY, JSON.stringify(reason));
  } catch (e) { /* sessionStorage unavailable (private mode) — non-fatal */ }

  setTimeout(() => {
    pendingAuthRedirect = false;

    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const targetSubdomain = reason && reason.subdomain;

    // Prefer the TARGET tenant when the backend told us which one it was.
    if (targetSubdomain) {
      const domainParts = parts.length > 2 ? parts.slice(1).join('.') : '';
      window.location.href = domainParts
        ? `https://${targetSubdomain}.${domainParts}/login`
        : '/login';
      return;
    }

    // For localhost or bare domains, redirect to normal login
    if (hostname.includes('localhost') || parts.length <= 2) {
      window.location.href = '/login';
    } else {
      // For subdomains like max_muscle.promotly.cloud, redirect to subdomain login
      const subdomain = parts[0];
      const domainParts = parts.slice(1).join('.');
      window.location.href = `https://${subdomain}.${domainParts}/login`;
    }
  }, 100);
};

/** Clear this tab's cached auth before a forced re-login. */
const clearLocalAuthState = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userData');
  localStorage.removeItem('keycloakRealmId');
  // Also clear redux-persist root so stale persisted auth cannot rehydrate
  try { localStorage.removeItem('persist:root'); } catch (e) {}

  if (store && clearAuthAction) {
    try {
      store.dispatch(clearAuthAction());
      console.log('Redux logout action dispatched successfully.');
    } catch (dispatchError) {
      console.error('Error dispatching logout action:', dispatchError);
    }
  }
};

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
    });

    if (error.response && error.response.status === 401) {
      const failedUrl = error.config?.url || '';
      const data = error.response.data || {};

      // ---------------------------------------------------------------------
      // ACCOUNT_CONTEXT_MISMATCH — checked FIRST, on ANY endpoint.
      //
      // The session belongs to a DIFFERENT tenant than the host being addressed
      // (one parent-domain session_id cookie is shared across every tenant). The
      // backend deliberately keeps the Redis session alive — a mismatch must not
      // log the user out of every tenant — so this is RECOVERABLE: clear this
      // tab's cached auth, stash a reason, and send them to the TARGET tenant's
      // login. Without this branch the 401 would fall through to the "non-auth
      // 401" policy below and be silently ignored (the original broken UX).
      // ---------------------------------------------------------------------
      if (data.code === 'ACCOUNT_CONTEXT_MISMATCH') {
        console.warn(
          'Account context mismatch — session belongs to another tenant. Forcing re-login.',
          { target: data.subdomain, targetAccountId: data.targetAccountId }
        );
        clearLocalAuthState();

        const targetSubdomain = data.subdomain || null;
        const currentSubdomain = window.location.hostname.split('.')[0];
        const onLoginPage = /login/.test(window.location.pathname);
        const alreadyOnTargetLogin = onLoginPage && targetSubdomain === currentSubdomain;

        if (!alreadyOnTargetLogin) {
          redirectToLogin({
            code: 'ACCOUNT_CONTEXT_MISMATCH',
            subdomain: targetSubdomain,
            targetAccountId: data.targetAccountId,
            at: Date.now(),
          });
        }
        return Promise.reject(error);
      }

      // ---------------------------------------------------------------------
      // Existing (unchanged) policy: only AUTH-endpoint 401s are treated as a
      // session failure. A 401 on a business/data endpoint (wrong role, missing
      // permission, expired resource token) must NOT log the user out mid-task —
      // that previously caused the cyclic reload loop.
      // ---------------------------------------------------------------------
      const isAuthCall =
        failedUrl.includes('/auth/status') ||
        failedUrl.includes('/auth/me') ||
        failedUrl.includes('/auth/login') ||
        failedUrl.includes('/auth/logout') ||
        failedUrl.includes('/auth/refresh') ||
        /\/auth\//.test(failedUrl);

      if (isAuthCall) {
        console.log('Unauthorized (401) on auth endpoint. Session expired or not authenticated.');
        clearLocalAuthState();

        // Check if this is a login attempt by checking the URL or current page
        const isLoginAttempt = failedUrl.includes('/auth/login');
        const isOnLoginPage = /login/.test(window.location.pathname);

        if (!isLoginAttempt && !isOnLoginPage) {
          console.log('Redirecting to login due to 401 Unauthorized on auth endpoint.');
          redirectToLogin(null);
        }
      } else {
        // Non-auth 401: leave state intact so the page's own error handling
        // (toast/alert) surfaces the specific failure.
        console.warn('Non-auth 401 received — NOT clearing session to avoid mid-task logout.', failedUrl);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
